import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO';
import { getPost, getRelatedPosts } from '../../data/blog';
import { serviceData } from '../../constants/services';
import { articleSchema } from '../../lib/site';

// internal links inside posts use the router (no full page reload)
const mdComponents = {
  a: ({ href = '', children }) =>
    href.startsWith('/') ? (
      <Link to={href}>{children}</Link>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <div className="frame flex min-h-[70vh] flex-col items-start justify-center gap-6 pt-32">
        <SEO title="Post not found | Farwings Tech Solutions" description="This blog post could not be found." noindex />
        <h1 className="display text-4xl sm:text-5xl">This post could not be found.</h1>
        <p className="text-muted">It may have moved. Try the blog index.</p>
        <Link to="/blog" className="btn-primary">
          Go to the blog
        </Link>
      </div>
    );
  }

  const related = getRelatedPosts(post);
  const service = serviceData[post.service];

  return (
    <>
      <SEO
        title={`${post.title} | Farwings Tech Solutions`}
        description={post.description}
        keywords={post.tags.join(', ')}
        type="article"
        article={{ published: post.date, modified: post.updated, section: post.category, tags: post.tags }}
        schema={articleSchema(post)}
        faq={post.faq}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
      />

      <article className="frame pt-36 pb-24 sm:pt-44">
        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-5xl"
        >
          <nav aria-label="Breadcrumb" className="text-sm text-muted">
            <Link to="/blog" className="text-accent link-line">
              Blog
            </Link>
            <span aria-hidden="true"> / </span>
            <span>{post.category}</span>
          </nav>
          <h1 className="mt-6 display text-4xl sm:text-6xl lg:text-[4.5rem]">{post.title}</h1>
          <p className="mt-6 text-muted">
            By Farwings Tech Solutions · <time dateTime={post.date}>{post.published}</time>
            {post.updated && post.updated !== post.date && (
              <>
                {' '}
                · Updated <time dateTime={post.updated}>{post.updatedLabel}</time>
              </>
            )}{' '}
            · {post.readMinutes} min read
          </p>
        </motion.header>

        <div className="mt-14 max-w-3xl border-t border-line pt-10">
          <aside aria-label="Summary" className="mb-10 rounded-2xl border border-line bg-raised p-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Quick answer</p>
            <p className="mt-2 text-lg text-ink/90">{post.summary}</p>
          </aside>

          <div className="prose prose-invert prose-lg prose-headings:font-bold prose-headings:[font-stretch:112%] prose-a:text-accent prose-p:text-ink/85 blog-content">
            <ReactMarkdown components={mdComponents}>{post.content}</ReactMarkdown>
          </div>

          {post.faq?.length > 0 && (
            <section aria-labelledby="post-faq" className="mt-16 border-t border-line pt-10">
              <h2 id="post-faq" className="display-semi text-3xl sm:text-4xl">
                Frequently asked questions
              </h2>
              <dl className="mt-6">
                {post.faq.map((f) => (
                  <div key={f.q} className="border-b border-line py-6">
                    <dt className="text-lg font-semibold stretch-semi">{f.q}</dt>
                    <dd className="mt-2 text-muted">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          <section className="mt-16 rounded-2xl border border-line p-8">
            <h2 className="display-semi text-2xl sm:text-3xl">Need help with this?</h2>
            <p className="mt-3 text-muted">
              Farwings Tech Solutions builds web apps, mobile apps, SaaS products and AI automation.{' '}
              {service && (
                <>
                  See our{' '}
                  <Link to={`/services/${post.service}`} className="text-accent link-line">
                    {service.heroTitle.toLowerCase()}
                  </Link>
                  .
                </>
              )}
            </p>
            <Link to="/contact" className="btn-primary mt-6">
              Talk to us
            </Link>
          </section>
        </div>

        {related.length > 0 && (
          <section aria-labelledby="related" className="mt-20">
            <h2 id="related" className="display-semi text-3xl">
              Keep reading
            </h2>
            <ul className="mt-6 border-t border-line">
              {related.map((r) => (
                <li key={r.slug} className="border-b border-line">
                  <Link to={`/blog/${r.slug}`} className="group flex flex-col gap-1 py-6 sm:flex-row sm:items-baseline sm:justify-between">
                    <span className="text-xl font-semibold stretch-semi transition-colors group-hover:text-accent">{r.title}</span>
                    <span className="text-sm text-muted">{r.published}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </>
  );
}
